// PRPricing.js
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle } from 'lucide-react';

const PRPricing = () => {
  const coverageItems = [
    'An eligibility check, followed by a recommendation on which route to take',
    'A document checklist built around your case, with guidance on preparing each item',
    'Completion and review of every application form',
    'Verification of the supporting evidence you supply',
    'Tuning your profile for Express Entry or the provincial stream you are using',
    'Help getting the application filed',
    'Follow-up and status tracking once it has been lodged',
    'Drafting replies to IRCC requests for further documents'
  ];

  const importantNotes = [
    'Government processing fees go straight to IRCC, that is Immigration, Refugees and Citizenship Canada',
    'Ivipass never collects government fees on your behalf',
    'Language test fees for IELTS, CELPIP and TEF are settled with the test provider',
    'Educational Credential Assessment (ECA) fees are paid to the designated organisation carrying out the assessment',
    'The decision on your PR sits with IRCC, not with Ivipass. Our part is making sure what reaches them lines up with what they ask for, fully documented and accurately claimed. We do not promise an outcome, and you should be wary of anyone who does.'
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
              What PR Support Costs With Us
            </h2>
          </motion.div>

          {/* Pricing Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white p-8 rounded-lg shadow-sm mb-6 border border-gray-200"
          >
            <h3 className="text-xl font-semibold text-primary mb-4">
              Permanent Residency Support Service
            </h3>
            <p className="text-gray-600 text-base font-light leading-relaxed mb-6">
              One service, running from the first eligibility check through to the day your permanent residency application is submitted.
            </p>

            {/* Coverage List */}
            <div className="mb-6">
              <p className="text-gray-700 text-base font-normal mb-4">
                Here is what the service covers:
              </p>
              <div className="space-y-3">
                {coverageItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 + (index * 0.1) }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 size={18} className="text-gray-700 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-base font-light">
                      {item}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Fee Structure */}
            <div className="bg-white p-6 rounded-lg mb-4 border border-gray-200">
              <p className="text-sm font-semibold text-primary mb-4">
                Service Fee
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-primary/5 to-transparent rounded-lg">
                  <div>
                    <p className="text-lg font-semibold text-primary">Full Application Support</p>
                    <p className="text-sm text-gray-600">Preparation and submission handled end to end</p>
                  </div>
                  <p className="text-2xl font-bold text-primary">$399</p>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Note:</span> the figure above buys our work on a single PR route. Anything beyond that, such as an application for a spouse or dependants, is charged separately.
                </p>
              </div>
            </div>

            <p className="text-gray-600 text-sm font-light leading-relaxed">
              We confirm the final figure at the end of your consultation, once the route and the scope of work are settled.
            </p>
          </motion.div>

          {/* Important Notes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-6"
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-base font-semibold text-primary">
                Important notes:
              </p>
            </div>
            <div className="space-y-2 ml-8">
              {importantNotes.map((note, index) => (
                <p key={index} className="text-gray-700 text-sm font-light leading-relaxed">
                  • {note}
                </p>
              ))}
            </div>
          </motion.div>

          {/* Government Fees Reference */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-6"
          >
            <h4 className="text-base font-semibold text-primary mb-3">
              Government Fees, Payable Direct to IRCC
            </h4>
            <div className="space-y-2 text-sm text-gray-700 font-light">
              <p>• <strong>Principal applicant:</strong> CAD $1,365</p>
              <p>• <strong>Spouse or partner:</strong> CAD $1,365</p>
              <p>• <strong>Each dependent child:</strong> CAD $230</p>
              <p>• <strong>Right of Permanent Residence Fee:</strong> CAD $515 for each adult</p>
              <p className="text-xs text-gray-600 mt-3">
                These amounts are revised from time to time. Check the IRCC website for the schedule in force today.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PRPricing;
